import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  InputAdornment,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Close,
  Search,
  Upload,
  PhotoCamera,
  DeleteOutline,
  CheckCircle,
  Warning,
  DirectionsCar,
  Add,
  CloudUpload,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { lookupVin, isValidVin, vehicleMakes, getModelsForMake, VehicleData } from '@/services/vinLookup';

interface AddVehicleDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (vehicleData: VehicleFormData) => Promise<void>;
}

interface VehicleFormData {
  vin?: string;
  make: string;
  model: string;
  year: number;
  trim?: string;
  licensePlate: string;
  type: 'sedan' | 'suv' | 'hatchback' | 'truck' | 'van' | 'luxury' | 'economy';
  color?: string;
  dailyRate: number;
  seats: number;
  mileage: number;
  engine?: string;
  transmission?: string;
  fuelType?: string;
  drivetrain?: string;
  doors?: number;
  description?: string;
  features?: string[];
  images?: File[];
  ownerId?: string;
  status: 'incomplete' | 'in_review' | 'active';
}

const vehicleTypes = [
  { value: 'sedan', label: 'Sedan' },
  { value: 'suv', label: 'SUV' },
  { value: 'hatchback', label: 'Hatchback' },
  { value: 'truck', label: 'Truck' },
  { value: 'van', label: 'Van' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'economy', label: 'Economy' }
];

const commonFeatures = [
  'Air Conditioning', 'Bluetooth', 'GPS Navigation', 'Backup Camera', 'Heated Seats',
  'Leather Seats', 'Sunroof', 'Cruise Control', 'Keyless Entry', 'USB Charging',
  'Apple CarPlay', 'Android Auto', 'Premium Sound', 'Parking Sensors', 'Lane Assist'
];

const steps = ['Vehicle Info', 'Specifications', 'Details & Features', 'Photos'];

export default function AddVehicleDialog({ open, onClose, onSubmit }: AddVehicleDialogProps) {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vinLookupLoading, setVinLookupLoading] = useState(false);
  const [vinInput, setVinInput] = useState('');
  const [vinLookupResult, setVinLookupResult] = useState<{ success: boolean; message: string } | null>(null);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  
  const [formData, setFormData] = useState<VehicleFormData>({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    licensePlate: '',
    type: 'sedan',
    dailyRate: 0,
    seats: 4,
    mileage: 0,
    features: [],
    images: [],
    status: 'incomplete'
  });

  const handleVinLookup = async () => {
    if (!vinInput || !isValidVin(vinInput)) {
      setVinLookupResult({
        success: false,
        message: 'Please enter a valid 17-character VIN'
      });
      return;
    }

    setVinLookupLoading(true);
    setVinLookupResult(null);

    try {
      const result = await lookupVin(vinInput);
      
      if (result.success && result.data) {
        setFormData(prev => ({
          ...prev,
          vin: result.data!.vin,
          make: result.data!.make || '',
          model: result.data!.model || '',
          year: result.data!.year || new Date().getFullYear(),
          trim: result.data!.trim,
          engine: result.data!.engine,
          transmission: result.data!.transmission,
          fuelType: result.data!.fuelType,
          doors: result.data!.doors,
          seats: result.data!.seats || 4
        }));
        
        setVinLookupResult({
          success: true,
          message: 'Vehicle information loaded successfully!'
        });
      } else {
        setVinLookupResult({
          success: false,
          message: result.error || 'Vehicle not found. Please enter details manually.'
        });
      }
    } catch (error) {
      setVinLookupResult({
        success: false,
        message: 'VIN lookup failed. Please enter details manually.'
      });
    } finally {
      setVinLookupLoading(false);
    }
  };

  const handleImageUpload = (files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files).slice(0, 10 - (formData.images?.length || 0));
    const currentImages = formData.images || [];
    
    setFormData(prev => ({
      ...prev,
      images: [...currentImages, ...newFiles]
    }));

    newFiles.forEach(file => {
      const url = URL.createObjectURL(file);
      setImagePreviewUrls(prev => [...prev, url]);
    });
  };

  const removeImage = (index: number) => {
    const currentImages = formData.images || [];
    const newImages = currentImages.filter((_, i) => i !== index);
    
    setFormData(prev => ({ ...prev, images: newImages }));
    
    if (imagePreviewUrls[index]) {
      URL.revokeObjectURL(imagePreviewUrls[index]);
    }
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const toggleFeature = (feature: string) => {
    const currentFeatures = formData.features || [];
    const newFeatures = currentFeatures.includes(feature)
      ? currentFeatures.filter(f => f !== feature)
      : [...currentFeatures, feature];
    
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    
    if (!formData.make || !formData.model || !formData.licensePlate) {
      console.error('Validation failed: Missing required fields');
      alert('Please fill in all required fields: Make, Model, and License Plate');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error('Failed to add vehicle:', error);
      alert(`Failed to add vehicle: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    setFormData({
      make: '',
      model: '',
      year: new Date().getFullYear(),
      licensePlate: '',
      type: 'sedan',
      dailyRate: 0,
      seats: 4,
      mileage: 0,
      features: [],
      images: [],
      status: 'incomplete'
    });
    setActiveStep(0);
    setVinInput('');
    setVinLookupResult(null);
    setImagePreviewUrls([]);
    onClose();
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return formData.make && formData.model && formData.year && formData.licensePlate;
      case 1:
        return formData.seats && formData.dailyRate;
      default:
        return true;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            {/* VIN Lookup */}
            <Card sx={{ mb: 3, bgcolor: theme.palette.primary.main + '08' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Search />
                  VIN Lookup (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter a VIN to automatically populate vehicle information
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Enter 17-character VIN"
                    value={vinInput}
                    onChange={(e) => setVinInput(e.target.value.toUpperCase())}
                    inputProps={{ maxLength: 17 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleVinLookup}
                    disabled={vinLookupLoading || !vinInput}
                    startIcon={vinLookupLoading ? <CircularProgress size={20} /> : <Search />}
                  >
                    Lookup
                  </Button>
                </Box>

                {vinLookupResult && (
                  <Alert 
                    severity={vinLookupResult.success ? 'success' : 'warning'} 
                    sx={{ mt: 2 }}
                  >
                    {vinLookupResult.message}
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Make *"
                  value={formData.make}
                  onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Model *"
                  value={formData.model}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Year *"
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                  inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="License Plate *"
                  value={formData.licensePlate}
                  onChange={(e) => setFormData(prev => ({ ...prev, licensePlate: e.target.value.toUpperCase() }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Vehicle Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Vehicle Type"
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                  >
                    {vehicleTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color"
                  value={formData.color || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Daily Rate ($) *"
                  type="number"
                  value={formData.dailyRate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dailyRate: parseFloat(e.target.value) }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Number of Seats *"
                  type="number"
                  value={formData.seats}
                  onChange={(e) => setFormData(prev => ({ ...prev, seats: parseInt(e.target.value) }))}
                  inputProps={{ min: 1, max: 15 }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mileage"
                  type="number"
                  value={formData.mileage}
                  onChange={(e) => setFormData(prev => ({ ...prev, mileage: parseInt(e.target.value) }))}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Engine"
                  value={formData.engine || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, engine: e.target.value }))}
                  placeholder="e.g., 2.5L 4-Cylinder"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Transmission"
                  value={formData.transmission || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, transmission: e.target.value }))}
                  placeholder="e.g., Automatic, Manual"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fuel Type"
                  value={formData.fuelType || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, fuelType: e.target.value }))}
                  placeholder="e.g., Gasoline, Electric"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the vehicle, its condition, and special features..."
              sx={{ mb: 3 }}
            />

            <Typography variant="h6" sx={{ mb: 2 }}>Features</Typography>
            <FormGroup>
              <Grid container spacing={1}>
                {commonFeatures.map((feature) => (
                  <Grid item xs={12} sm={6} md={4} key={feature}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.features?.includes(feature) || false}
                          onChange={() => toggleFeature(feature)}
                        />
                      }
                      label={feature}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Vehicle Photos</Typography>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleImageUpload(e.target.files)}
              style={{ display: 'none' }}
            />
            
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: 'divider',
                cursor: 'pointer',
                mb: 3,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Click to upload vehicle photos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maximum 10 images. JPG, PNG up to 10MB each
              </Typography>
            </Paper>

            {imagePreviewUrls.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Uploaded Photos ({imagePreviewUrls.length}/10)
                </Typography>
                <Grid container spacing={2}>
                  {imagePreviewUrls.map((url, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Card>
                        <Box sx={{ position: 'relative' }}>
                          <img
                            src={url}
                            alt={`Vehicle photo ${index + 1}`}
                            style={{ width: '100%', height: 120, objectFit: 'cover' }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeImage(index)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.dark' }
                            }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <DirectionsCar />
          </Avatar>
          <Typography variant="h6">Add New Vehicle</Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, py: 1 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isSubmitting}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!isStepValid(activeStep)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || !isStepValid(activeStep)}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Add />}
          >
            {isSubmitting ? 'Adding Vehicle...' : 'Add Vehicle'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}